import React, { useState, useEffect, useCallback } from 'react';

interface DiscoveryFormData {
  clientId: number;
  projectType: string;

  // Business Foundation & Context
  businessModel: string;
  businessObjectives: string;
  marketingInitiativeTrigger: string;
  successMetrics: string;

  // Target Audience & Market Analysis
  idealCustomer: string;
  customerPainPoints: string;
  customerSolutionSeeking: string;
  purchaseDecisionProcess: string;
  topCompetitors: string;
  competitorStrengths: string;
  marketGaps: string;
  externalFactors: string;

  // Brand Positioning & Differentiation
  businessChoice: string;
  uniqueDifferentiator: string;
  customerOutcomes: string;
  missingIfGone: string;
  desiredPerception: string;
  brandPersonality: string;
  toneStyle: string;
  avoidWords: string;
  admiredBrands: string;

  // Current Marketing Assessment
  currentMarketing: string;
  successfulEfforts: string;
  marketingAssets: string;
  marketingBudget: string;
  performanceTracking: string;
  kpis: string;
  customerAcquisitionCost: string;
  leadSources: string;

  // Goals & Success Metrics
  marketingGoals: string;
  alignment: string;
  wildlySuccessful: string;
  mustHaveOutcomes: string;
  measurementMethod: string;
  analyticsCapabilities: string;
  reviewFrequency: string;
  earlyIndicators: string;

  // Resource & Constraint Analysis
  budgetAmount: string;
  budgetAllocation: string;
  timelineConstraints: string;
  seasonalConsiderations: string;
  teamInvolvement: string;
  teamStrengthsGaps: string;
  currentTools: string;
  approvalProcesses: string;

  completedAt?: string;
  lastModified?: string;
}

interface StrategicDiscoveryFormProps {
  clientId: number;
  projectType: string;
  onClose: () => void;
  onSave?: () => void;
}

const StrategicDiscoveryForm: React.FC<StrategicDiscoveryFormProps> = ({ clientId, projectType, onClose, onSave }) => {
  const [formData, setFormData] = useState<DiscoveryFormData>({
    clientId,
    projectType,
    businessModel: '',
    businessObjectives: '',
    marketingInitiativeTrigger: '',
    successMetrics: '',
    idealCustomer: '',
    customerPainPoints: '',
    customerSolutionSeeking: '',
    purchaseDecisionProcess: '',
    topCompetitors: '',
    competitorStrengths: '',
    marketGaps: '',
    externalFactors: '',
    businessChoice: '',
    uniqueDifferentiator: '',
    customerOutcomes: '',
    missingIfGone: '',
    desiredPerception: '',
    brandPersonality: '',
    toneStyle: '',
    avoidWords: '',
    admiredBrands: '',
    currentMarketing: '',
    successfulEfforts: '',
    marketingAssets: '',
    marketingBudget: '',
    performanceTracking: '',
    kpis: '',
    customerAcquisitionCost: '',
    leadSources: '',
    marketingGoals: '',
    alignment: '',
    wildlySuccessful: '',
    mustHaveOutcomes: '',
    measurementMethod: '',
    analyticsCapabilities: '',
    reviewFrequency: '',
    earlyIndicators: '',
    budgetAmount: '',
    budgetAllocation: '',
    timelineConstraints: '',
    seasonalConsiderations: '',
    teamInvolvement: '',
    teamStrengthsGaps: '',
    currentTools: '',
    approvalProcesses: '',
  });

  const [activeSection, setActiveSection] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchExistingData = useCallback(async () => {
    try {
      const response = await fetch(`http://localhost:8080/api/discovery/${clientId}/${projectType}`);
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setFormData(data);
        }
      }
    } catch (error) {
      console.error('Error fetching discovery data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, projectType]);

  useEffect(() => {
    // Load existing discovery data if available
    fetchExistingData();
  }, [fetchExistingData]);

  const handleInputChange = (field: keyof DiscoveryFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (isComplete: boolean = false) => {
    setIsSaving(true);
    try {
      const dataToSave = {
        ...formData,
        lastModified: new Date().toISOString(),
        ...(isComplete && { completedAt: new Date().toISOString() })
      };

      const response = await fetch('http://localhost:8080/api/discovery', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSave),
      });

      if (response.ok) {
        if (isComplete) {
          onSave?.();
          onClose();
        } else {
          alert('Progress saved successfully!');
        }
      } else {
        alert('Failed to save discovery data');
      }
    } catch (error) {
      console.error('Error saving discovery data:', error);
      alert('Error saving discovery data');
    } finally {
      setIsSaving(false);
    }
  };

  const sections = [
    {
      title: 'Business Foundation & Context',
      icon: '🏢',
      fields: [
        { label: 'What is your primary business model and revenue streams?', key: 'businessModel' as keyof DiscoveryFormData },
        { label: 'What are your top 3 business objectives for the next 12-24 months?', key: 'businessObjectives' as keyof DiscoveryFormData },
        { label: 'What specific challenges or opportunities triggered this marketing initiative?', key: 'marketingInitiativeTrigger' as keyof DiscoveryFormData },
        { label: 'How do you currently measure business success?', key: 'successMetrics' as keyof DiscoveryFormData },
      ]
    },
    {
      title: 'Target Audience & Market Analysis',
      icon: '🎯',
      fields: [
        { label: 'Who is your ideal customer? (Demographics, psychographics, behaviors)', key: 'idealCustomer' as keyof DiscoveryFormData },
        { label: 'What are their primary pain points or unmet needs?', key: 'customerPainPoints' as keyof DiscoveryFormData },
        { label: 'Where do they currently seek solutions like yours?', key: 'customerSolutionSeeking' as keyof DiscoveryFormData },
        { label: 'How do they prefer to research and make purchasing decisions?', key: 'purchaseDecisionProcess' as keyof DiscoveryFormData },
        { label: 'Who are your top 3-5 direct competitors?', key: 'topCompetitors' as keyof DiscoveryFormData },
        { label: 'What are they doing well in their marketing?', key: 'competitorStrengths' as keyof DiscoveryFormData },
        { label: 'Where do you see gaps or opportunities in the market?', key: 'marketGaps' as keyof DiscoveryFormData },
        { label: 'What external factors (trends, regulations, technology) impact your industry?', key: 'externalFactors' as keyof DiscoveryFormData },
      ]
    },
    {
      title: 'Brand Positioning & Differentiation',
      icon: '💎',
      fields: [
        { label: 'Why did you choose this business? What do you truly care about that influenced this decision?', key: 'businessChoice' as keyof DiscoveryFormData },
        { label: 'What makes your offering genuinely different from alternatives?', key: 'uniqueDifferentiator' as keyof DiscoveryFormData },
        { label: 'What specific outcomes or benefits do customers achieve with you?', key: 'customerOutcomes' as keyof DiscoveryFormData },
        { label: 'What would customers miss most if your business didn\'t exist?', key: 'missingIfGone' as keyof DiscoveryFormData },
        { label: 'How do you want to be perceived versus competitors?', key: 'desiredPerception' as keyof DiscoveryFormData },
        { label: 'If your brand were a person, how would you describe their personality?', key: 'brandPersonality' as keyof DiscoveryFormData },
        { label: 'What tone and style resonates with your audience?', key: 'toneStyle' as keyof DiscoveryFormData },
        { label: 'What words or phrases should never be associated with your brand?', key: 'avoidWords' as keyof DiscoveryFormData },
        { label: 'What brands (in any industry) do you admire and why?', key: 'admiredBrands' as keyof DiscoveryFormData },
      ]
    },
    {
      title: 'Current Marketing Assessment',
      icon: '📊',
      fields: [
        { label: 'What marketing activities are you currently doing?', key: 'currentMarketing' as keyof DiscoveryFormData },
        { label: 'Which efforts have been most/least successful and why?', key: 'successfulEfforts' as keyof DiscoveryFormData },
        { label: 'What marketing assets do you have (website, content, email lists, etc.)?', key: 'marketingAssets' as keyof DiscoveryFormData },
        { label: 'What\'s your current marketing budget and resource allocation?', key: 'marketingBudget' as keyof DiscoveryFormData },
        { label: 'How do you currently track marketing performance?', key: 'performanceTracking' as keyof DiscoveryFormData },
        { label: 'What are your key performance indicators (KPIs)?', key: 'kpis' as keyof DiscoveryFormData },
        { label: 'What\'s your current customer acquisition cost and lifetime value?', key: 'customerAcquisitionCost' as keyof DiscoveryFormData },
        { label: 'Where do most of your leads/customers come from today?', key: 'leadSources' as keyof DiscoveryFormData },
      ]
    },
    {
      title: 'Goals & Success Metrics',
      icon: '🎯',
      fields: [
        { label: 'What are your specific marketing goals for the next 6-12 months?', key: 'marketingGoals' as keyof DiscoveryFormData },
        { label: 'How do these align with your overall business objectives?', key: 'alignment' as keyof DiscoveryFormData },
        { label: 'What would "wildly successful" marketing look like for you?', key: 'wildlySuccessful' as keyof DiscoveryFormData },
        { label: 'What are your must-have vs. nice-to-have outcomes?', key: 'mustHaveOutcomes' as keyof DiscoveryFormData },
        { label: 'How will we measure success for each objective?', key: 'measurementMethod' as keyof DiscoveryFormData },
        { label: 'What data and analytics capabilities do you have?', key: 'analyticsCapabilities' as keyof DiscoveryFormData },
        { label: 'How often do you want to review performance?', key: 'reviewFrequency' as keyof DiscoveryFormData },
        { label: 'What early indicators will show we\'re on the right track?', key: 'earlyIndicators' as keyof DiscoveryFormData },
      ]
    },
    {
      title: 'Resource & Constraint Analysis',
      icon: '⚙️',
      fields: [
        { label: 'What is your marketing budget for this initiative?', key: 'budgetAmount' as keyof DiscoveryFormData },
        { label: 'How is budget typically allocated (channels, tools, agencies, etc.)?', key: 'budgetAllocation' as keyof DiscoveryFormData },
        { label: 'What are your key timeline constraints or deadlines?', key: 'timelineConstraints' as keyof DiscoveryFormData },
        { label: 'Are there seasonal considerations we need to account for?', key: 'seasonalConsiderations' as keyof DiscoveryFormData },
        { label: 'Who on your team will be involved in executing marketing activities?', key: 'teamInvolvement' as keyof DiscoveryFormData },
        { label: 'What are your team\'s strengths and skill gaps?', key: 'teamStrengthsGaps' as keyof DiscoveryFormData },
        { label: 'What tools and technology do you currently use?', key: 'currentTools' as keyof DiscoveryFormData },
        { label: 'What approval processes do we need to consider?', key: 'approvalProcesses' as keyof DiscoveryFormData },
      ]
    },
  ];

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading discovery form...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '16px',
      overflowY: 'auto'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        maxWidth: '1400px',
        width: '95%',
        margin: '32px 0',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Strategic Discovery</h2>
              <p className="text-blue-100 mt-1">Client Interview & Discovery Questionnaire</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl leading-none"
            >
              ×
            </button>
          </div>

          {/* Section Navigation */}
          <div style={{
            marginTop: '24px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0',
            borderBottom: '1px solid rgba(255, 255, 255, 0.3)'
          }}>
            {sections.map((section, index) => (
              <button
                key={index}
                onClick={() => setActiveSection(index)}
                style={{
                  padding: '12px 20px',
                  fontSize: '13px',
                  lineHeight: '1.4',
                  flex: '1 1 auto',
                  minWidth: '150px',
                  transition: 'all 0.2s',
                  backgroundColor: activeSection === index ? 'white' : 'transparent',
                  color: activeSection === index ? '#2563eb' : '#1f2937',
                  fontWeight: activeSection === index ? '600' : '500',
                  borderBottom: activeSection === index ? '3px solid #2563eb' : '3px solid transparent',
                  border: 'none',
                  borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  if (activeSection !== index) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSection !== index) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {section.title}
              </button>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div style={{
          padding: '24px',
          flex: '1 1 auto',
          overflowY: 'auto',
          minHeight: '0'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            <div>
              <h3 className="text-xl font-bold text-gray-800">{sections[activeSection].title}</h3>
            </div>

            {sections[activeSection].fields.map((field, index) => (
              <div key={index} style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
                <label style={{
                  width: '50%',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  paddingTop: '8px'
                }}>
                  {field.label}
                </label>
                <textarea
                  value={formData[field.key] as string}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  style={{
                    width: '50%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    resize: 'vertical',
                    minHeight: '80px',
                    fontFamily: 'inherit'
                  }}
                  placeholder="Enter your response..."
                />
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-6 bg-gray-50 rounded-b-lg">
          <div className="flex items-center justify-between">
            <div className="flex gap-3">
              <button
                onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                disabled={activeSection === 0}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>
              <button
                onClick={() => setActiveSection(Math.min(sections.length - 1, activeSection + 1))}
                disabled={activeSection === sections.length - 1}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => handleSave(false)}
                disabled={isSaving}
                style={{
                  padding: '8px 16px',
                  color: '#4b5563',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.5 : 1,
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => !isSaving && (e.currentTarget.style.color = '#1f2937')}
                onMouseLeave={(e) => !isSaving && (e.currentTarget.style.color = '#4b5563')}
              >
                Save Progress
              </button>
              <button
                onClick={() => handleSave(true)}
                disabled={isSaving}
                style={{
                  padding: '8px 16px',
                  color: '#2563eb',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.5 : 1,
                  fontWeight: '600',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => !isSaving && (e.currentTarget.style.color = '#1e40af')}
                onMouseLeave={(e) => !isSaving && (e.currentTarget.style.color = '#2563eb')}
              >
                {isSaving ? 'Saving...' : 'Complete & Save'}
              </button>
            </div>
          </div>

          <div className="mt-4 text-xs text-gray-500 text-center">
            Section {activeSection + 1} of {sections.length}
            {formData.lastModified && ` • Last saved: ${new Date(formData.lastModified).toLocaleString()}`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategicDiscoveryForm;
